import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { QueryKeys } from "queryClient";
import {
  CardmarketCalcTaskPage,
  getCardmarketCalcTaskList,
  getCardmarketCalcTaskListByExpansion,
} from "modules/cardmarketcalctasks/api";
import { useCardmarketCalcTaskListFilterAndSort } from "../useCardmarketCalcTaskListFilterAndSort";

type CardmarketCalcTaskListParamsType = {
  expansionID: string;
};

/**
 * Seitenparameter im Cache. loadAll steckt bewusst mit drin: bei einer
 * Invalidierung lädt TanStack jede Seite mit ihrem Parameter neu, und eine per
 * "Alle laden" geholte Restseite soll dann wieder vollständig sein statt auf 50
 * Zeilen zusammenzufallen.
 */
interface PageParam {
  token: string | null;
  loadAll: boolean;
}

type CardmarketCalcTaskData = InfiniteData<CardmarketCalcTaskPage, PageParam>;

/**
 * Online-Preisberechnungen seitenweise (50 je Seite, neueste zuerst), optional
 * auf die Edition aus der Route eingeschränkt.
 *
 * Filter und Sortierung laufen wie bisher clientseitig, also über die bereits
 * geladenen Seiten - dasselbe Verhalten wie bei den Sync- und Bestandsaufgaben.
 * "Alle laden" holt den Rest in einem Zug.
 */
export const useCardmarketCalcTaskList = () => {
  const { expansionID } = useParams<CardmarketCalcTaskListParamsType>();
  const queryClient = useQueryClient();

  const filterAndSortCardmarketCalcTasks =
    useCardmarketCalcTaskListFilterAndSort();

  // Die Edition gehört in den Schlüssel; die Invalidierung über das Präfix
  // [CardmarketCalcTasks] trifft weiterhin beide Varianten.
  const queryKey = useMemo(
    () => [QueryKeys.CardmarketCalcTasks, expansionID ?? "all"],
    [expansionID],
  );

  const fetchPage = useCallback(
    ({ token, loadAll }: PageParam) =>
      expansionID
        ? getCardmarketCalcTaskListByExpansion(expansionID, token, loadAll)
        : getCardmarketCalcTaskList(token, loadAll),
    [expansionID],
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery<
      CardmarketCalcTaskPage,
      Error,
      CardmarketCalcTaskData,
      typeof queryKey,
      PageParam
    >({
      queryKey,
      queryFn: ({ pageParam }) => fetchPage(pageParam),
      initialPageParam: { token: null, loadAll: false },
      getNextPageParam: (lastPage) =>
        lastPage.nextToken
          ? { token: lastPage.nextToken, loadAll: false }
          : undefined,
      staleTime: 1000 * 60 * 15, // 15 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
    });

  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const loadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage]);

  const loadAll = useCallback(async () => {
    const current = queryClient.getQueryData<CardmarketCalcTaskData>(queryKey);
    const lastToken = current?.pages.at(-1)?.nextToken ?? null;

    if (!current || !lastToken || isLoadingAll) {
      return;
    }

    setIsLoadingAll(true);

    try {
      const pageParam: PageParam = { token: lastToken, loadAll: true };
      const rest = await fetchPage(pageParam);

      queryClient.setQueryData<CardmarketCalcTaskData>(queryKey, (old) =>
        old
          ? {
              pages: [...old.pages, rest],
              pageParams: [...old.pageParams, pageParam],
            }
          : old,
      );
    } finally {
      setIsLoadingAll(false);
    }
  }, [fetchPage, isLoadingAll, queryClient, queryKey]);

  const cardmarketCalcTaskList = useMemo(
    () =>
      filterAndSortCardmarketCalcTasks(
        data?.pages.flatMap((page) => page.items) ?? [],
      ),
    [data, filterAndSortCardmarketCalcTasks],
  );

  return {
    cardmarketCalcTaskList,
    expansionID,
    isLoading,
    isLoadingMore: isFetchingNextPage || isLoadingAll,
    hasNextData: Boolean(hasNextPage),
    loadMore,
    loadAll,
  };
};
